import { useCallback, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { usePatientStore } from '../stores/patient';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface ConsultationRoom {
  roomUrl: string;
  token: string;
  doctorName?: string;
  doctorSpecialty?: string;
}

interface UseConsultationReturn {
  room: ConsultationRoom | null;
  isLoading: boolean;
  error: Error | null;
  startTime: Date | null;
  duration: number;
  fetchRoom: () => Promise<void>;
  endConsultation: () => Promise<void>;
  submitFeedback: (rating: number, comment?: string) => Promise<void>;
}

export function useConsultation(): UseConsultationReturn {
  const { consultation, setConsultation } = usePatientStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [duration, setDuration] = useState(0);

  // Fetch room details from API
  const fetchRoom = useCallback(async () => {
    const consultationId = consultation?.id;
    if (!consultationId) {
      setError(new Error('No consultation ID available'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const patientId = consultation?.id ? usePatientStore.getState().patient?.id : undefined;
      const url = new URL(`${API_BASE_URL}/api/v1/consultations/${consultationId}/room`);
      if (patientId) {
        url.searchParams.set('patientId', patientId);
      }

      const response = await fetch(url.toString());

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to fetch room: ${response.statusText}`);
      }

      const json = await response.json();
      // Unwrap API response envelope: { data: { roomUrl, token, consultationId, participant } }
      const data = json.data ?? json;

      setConsultation({
        ...consultation,
        roomUrl: data.roomUrl,
        token: data.token,
        doctorName: data.participant?.name || data.doctorName,
        doctorSpecialty: data.doctorSpecialty,
        startedAt: new Date().toISOString(),
      });

      setStartTime(new Date());
      toast.success('Connected to consultation room');
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error');
      setError(errorObj);
      toast.error(errorObj.message);
    } finally {
      setIsLoading(false);
    }
  }, [consultation, setConsultation]);

  // End the consultation
  const endConsultation = useCallback(async () => {
    const consultationId = consultation?.id;
    if (!consultationId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/consultations/${consultationId}/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endedBy: 'patient',
          durationSeconds: duration,
        }),
      });

      if (!response.ok) {
        console.error('Failed to end consultation:', response.statusText);
      }
    } catch (err) {
      console.error('Failed to end consultation:', err);
    }
  }, [consultation?.id, duration]);

  // Submit feedback
  const submitFeedback = useCallback(async (rating: number, comment?: string) => {
    const consultationId = consultation?.id;
    if (!consultationId) {
      throw new Error('No consultation ID available');
    }

    const response = await fetch(
      `${API_BASE_URL}/api/v1/consultations/${consultationId}/feedback`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating,
          comment,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `Failed to submit feedback: ${response.statusText}`);
    }
  }, [consultation?.id]);

  // Track call duration
  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Initialize start time from consultation data
  useEffect(() => {
    if (consultation?.startedAt && !startTime) {
      setStartTime(new Date(consultation.startedAt));
    }
  }, [consultation?.startedAt, startTime]);

  return {
    room: consultation
      ? {
          roomUrl: consultation.roomUrl,
          token: consultation.token,
          doctorName: consultation.doctorName,
          doctorSpecialty: consultation.doctorSpecialty,
        }
      : null,
    isLoading,
    error,
    startTime,
    duration,
    fetchRoom,
    endConsultation,
    submitFeedback,
  };
}

// Helper function to format duration as MM:SS
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
